<template>
  <div class="account">
    <VTabs v-model="activeTab">
      <VTab value="general">{{ $t("general") }}</VTab>
      <VTab value="boost">Boost!</VTab>
      <VTab value="subsc">{{ $t("subscription") }}</VTab>
    </VTabs>

    <VWindow v-model="activeTab">
      <VWindowItem value="general">
        <div v-if="loadingUser" class="account__loading">
          <VProgressCircular indeterminate color="primary" />
        </div>
        <template v-if="!loadingUser">
          <section class="settings-card">
            <div class="settings-card__header">
              <h3>{{ $t("general_info") }}</h3>
            </div>
            <div class="account__profile-row">
              <span class="account__profile-label">{{ $t("firstname") }}</span>
              <span>{{ user.name || "—" }}</span>
            </div>
            <div class="account__profile-row">
              <span class="account__profile-label">Email</span>
              <span>{{ user.email }}</span>
            </div>
          </section>

          <section class="settings-card">
            <div class="settings-card__header">
              <h3>{{ $t("change_password") }}</h3>
            </div>
            <v-form ref="form" v-model="valid">
              <VTextField
                v-model="oldPassword"
                :rules="passwordRules"
                :label="$t('old_password')"
                type="password"
                :disabled="loading"
              />
              <v-text-field
                v-model="password"
                :rules="passwordRules"
                :label="$t('new_password')"
                :hint="$t('booking.password_hint')"
                persistent-hint
                type="password"
                :disabled="loading"
              />
              <v-text-field
                v-model="passwordConfirm"
                :rules="passwordRules"
                :label="$t('confirm_new_password')"
                type="password"
                :disabled="loading"
                class="mt-4"
              />
              <v-btn
                :disabled="!valid"
                @click="updateInfo"
                :loading="loading"
                color="#403838"
                variant="flat"
                class="mt-4"
                >{{ $t("change") }}</v-btn
              >
            </v-form>
          </section>
        </template>
      </VWindowItem>

      <VWindowItem value="boost">
        <section class="settings-card account__boost">
          <img :src="boostImg" alt="Boost Image" />
          <div class="boost__text">
            <h3>Функція Boost доступна лише в підписці Бізнес PRO.</h3>
            <p>
              З послугою Boost профіль Вашої компанії автоматично потрапляє в
              рекомендації на головній сторінці додатку на два тижні. У вас
              також з’явиться можливість додати ваучери для покупки та
              розігрувати GIFT карти серед своїх клієнтів.
            </p>
          </div>
        </section>
      </VWindowItem>

      <VWindowItem value="subsc">
        <section class="settings-card">
          <div class="settings-card__header">
            <h3>Підписка</h3>
          </div>
          <div v-if="user.subscription">
            <h1>{{ user.subscription.plan }}</h1>
            <p>
              {{ $t("status") }}:
              {{ getSubscriptionStatus(user.subscription.status) }}
            </p>
            <p>
              {{ $t("start_date") }}:
              {{ humanizeDate(user.subscription.start_date) }}
            </p>
            <p>
              {{ $t("end_date") }}: {{ humanizeDate(user.subscription.end_date) }}
            </p>
          </div>
          <div v-else>
            <p>{{ $t("dont_have_subscription") }}:</p>
            <div class="plans-wrapper">
              <div v-for="plan in plans" :key="plan.id" class="plan">
                <p>{{ plan.name }}</p>
                <p>{{ plan.price }}</p>
                <p>{{ plan.description }}</p>
                <v-btn
                  @click="handleCreateSubscription(plan.id)"
                  :loading="loadingSubscription"
                  color="#403838"
                  variant="flat"
                >
                  {{ textSubscriptionBtn }}
                </v-btn>
              </div>
            </div>
          </div>
        </section>
      </VWindowItem>
    </VWindow>
  </div>
</template>

<script setup lang="ts">
import {
  getCurrentUser,
  getPlans,
  updateCurrentUserPassword,
  createSubscription,
} from "@/main/services/api.service";
import { User } from "@/main/types/user.type";
import { onMounted, ref } from "vue";
import { humanizeDate } from "@/main/helpers/utils.helper";

import boostImg from "@/main/assets/img/boost.svg";

const activeTab = ref(0);
const user = ref<User>({
  name: "",
  email: "",
  subscription: {
    id: 0,
    plan_id: 0,
    paid: false,
    start_date: "",
    end_date: "",
    status: "",
    plan: "",
  },
  username: "",
  first_name: "",
  last_name: "",
  phone_number: "",
  address: "",
  avatar: "",
});
const password = ref("");
const oldPassword = ref("");
const passwordConfirm = ref("");
const phoneNumber = ref("");
const valid = ref(false);
const loading = ref(false);
const loadingUser = ref(false);
const loadingSubscription = ref(false);
const plans = ref<
  {
    id: number;
    name: string;
    price: number;
    description: string;
  }[]
>([]);

const textSubscriptionBtn = ref("Підписатися");

onMounted(async () => {
  loadingUser.value = true;
  plans.value = await getPlans();
  user.value = await getCurrentUser();
  loadingUser.value = false;
});

const passwordRules = [
  (v: any) => !!v || "Поле обов'язкове",
  (v: string | any[]) =>
    v.length >= 6 || "Пароль повинен бути не менше 6 символів",
];
const phoneRules = [
  (v: any) => !!v || "Phone number is required",
  (v: string) => /^\d{9}$/.test(v) || "Phone number must be 10 digits",
];
const availableSubscriptions = ref([
  { id: 1, name: "Basic Plan" },
  { id: 2, name: "Premium Plan" },
  { id: 3, name: "Pro Plan" },
]);

const getSubscriptionStatus = (status: string) => {
  switch (status) {
    case "subscribed":
      return "Активний";
    case "unsubscribed":
      return "Неактивний";
    case "failed":
      return "Скасований";
    case "error":
      return "Помилка";
    default:
      return "Не визначено";
  }
};

const updateInfo = async () => {
  loading.value = true;
  const result = await updateCurrentUserPassword(
    oldPassword.value,
    password.value,
    passwordConfirm.value
  );

  if (result) {
    password.value = "";
    oldPassword.value = "";
    passwordConfirm.value = "";
  } else {
    console.log("Error updating user information");
  }
  loading.value = false;
};

const handleCreateSubscription = async (planId: number) => {
  loadingSubscription.value = true;
  try {
    const { url } = await createSubscription(planId);

    if (url) {
      textSubscriptionBtn.value = "Перенаправлення...";
      setTimeout(() => {
        window.location.replace(url);
      }, 2000);
    }
  } catch (error) {
    console.log("Error creating subscription", error);
  }
  loadingSubscription.value = false;
};
</script>

<style scoped lang="scss">
@use "../../styles/_breakpoints" as bp;

.account {
  width: 100%;
  height: auto;
  padding: 32px;

  @include bp.tablet {
    padding: 16px;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 40px;
  }

  &__profile-row {
    display: flex;
    column-gap: 8px;
    font-size: 14px;
    padding: 6px 0;

    & + & {
      border-top: 1px solid #eceef1;
    }
  }

  &__profile-label {
    min-width: 120px;
    color: #696e7c;
    font-weight: 600;
  }

  &__boost {
    display: flex;
    justify-content: center;
    align-items: center;

    // Side-by-side image + text has no room to breathe on a phone - the
    // text was squeezing into a sliver next to the full-size illustration.
    @include bp.tablet {
      flex-direction: column;
    }

    img {
      max-width: 200px;

      @include bp.tablet {
        max-width: 140px;
      }
    }

    .boost__text {
      margin-top: 16px;
      text-align: center;
    }
  }

}

.settings-card {
  background: #f7f8fa;
  border: 1px solid #eceef1;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    h3 {
      color: #403838;
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
  }
}

.plans-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 16px;

  @include bp.tablet {
    flex-direction: column;
  }
}

.plan {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  width: 30%;

  @include bp.tablet {
    width: 100%;
  }
}
</style>
